
import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { BarChart3, RefreshCw, Save, CheckCircle, AlertCircle, Search, Globe, Shield } from 'lucide-react';
import { useSiteConfigValue } from '@/hooks/useSiteConfigValue';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';

const AdminSiteConfigSEOSettings = () => {
  const { toast } = useToast();
  const [saving, setSaving] = useState<string | null>(null);

  // Search Engine Verification Codes
  const verificationConfigs = [
    { 
      key: 'google_site_verification', 
      label: 'Google Site Verification', 
      type: 'input',
      placeholder: 'Enter verification code from Google Search Console',
      description: 'Get this from Google Search Console → Settings → Ownership verification',
      icon: Search,
      critical: true
    },
    { 
      key: 'bing_site_verification', 
      label: 'Bing Webmaster Verification', 
      type: 'input',
      placeholder: 'Enter verification code from Bing Webmaster Tools',
      description: 'Get this from Bing Webmaster Tools → Configure My Site → Verify Ownership',
      icon: Globe,
      critical: true
    },
    { 
      key: 'yandex_verification', 
      label: 'Yandex Verification', 
      type: 'input',
      placeholder: 'Enter Yandex verification code (optional)',
      description: 'For Russian search engine coverage',
      icon: Globe,
      critical: false
    },
    { 
      key: 'pinterest_verification', 
      label: 'Pinterest Domain Verification', 
      type: 'input',
      placeholder: 'Enter Pinterest verification code (optional)',
      description: 'For Pinterest rich pins and domain verification',
      icon: Globe,
      critical: false
    }
  ];

  // SEO Meta Settings
  const seoConfigs = [
    { 
      key: 'meta_description', 
      label: 'Default Meta Description', 
      type: 'textarea',
      placeholder: 'Brief description of your website for search engines (max 160 chars)',
      description: 'Used as fallback when pages don\'t have specific descriptions'
    },
    { 
      key: 'meta_keywords', 
      label: 'Default Meta Keywords', 
      type: 'input',
      placeholder: 'keyword1, keyword2, keyword3',
      description: 'Comma-separated list of default keywords'
    },
    { 
      key: 'og_image_url', 
      label: 'Default OG Image URL', 
      type: 'input',
      placeholder: 'https://thebulkimagegenerator.com/og-image.png',
      description: 'Default social share image (1200x630px recommended)'
    },
    { 
      key: 'twitter_handle', 
      label: 'Twitter/X Handle', 
      type: 'input',
      placeholder: '@bulkimagegen',
      description: 'Your Twitter/X handle for social cards'
    }
  ];

  // Analytics Settings
  const analyticsConfigs = [
    { 
      key: 'google_analytics_id', 
      label: 'Google Analytics ID', 
      type: 'input',
      placeholder: 'G-XXXXXXXXXX or UA-XXXXXXXXX',
      description: 'Google Analytics 4 or Universal Analytics ID'
    },
    { 
      key: 'google_tag_manager_id', 
      label: 'Google Tag Manager ID', 
      type: 'input',
      placeholder: 'GTM-XXXXXXX',
      description: 'For advanced tag management'
    },
    { 
      key: 'facebook_pixel_id', 
      label: 'Facebook Pixel ID', 
      type: 'input',
      placeholder: 'XXXXXXXXXXXXXXX',
      description: 'For Facebook/Meta advertising tracking'
    },
    { 
      key: 'tiktok_pixel_id', 
      label: 'TikTok Pixel ID', 
      type: 'input',
      placeholder: 'XXXXXXXXXXXXXXX',
      description: 'For TikTok advertising tracking'
    }
  ];

  interface ConfigItem {
    key: string;
    label: string;
    type: string;
    placeholder: string;
    description?: string;
    icon?: React.ComponentType<{ className?: string }>;
    critical?: boolean;
  }

  const SEOConfigCard = ({ config }: { config: ConfigItem }) => {
    const { value, loading, refetch } = useSiteConfigValue(config.key);
    const [localValue, setLocalValue] = useState('');
    const [hasChanged, setHasChanged] = useState(false);

    React.useEffect(() => {
      if (value !== null && value !== localValue) {
        setLocalValue(value);
        setHasChanged(false);
      }
    }, [value]);

    const handleInputChange = (newValue: string) => {
      setLocalValue(newValue);
      setHasChanged(newValue !== (value || ''));
    };

    const handleUpdate = async () => {
      setSaving(config.key);
      try {
        console.log(`Updating ${config.key} to:`, localValue);
        
        const { error } = await supabase.rpc('update_site_config', {
          config_key: config.key,
          new_value: localValue,
          admin_email: null,
          change_reason: `Updated ${config.label} via Admin Panel`
        });

        if (error) throw error;

        toast({
          title: "Success",
          description: `${config.label} updated successfully`,
        });
        
        setHasChanged(false);
        await refetch();
      } catch (error) {
        console.error(`Error updating ${config.key}:`, error);
        toast({
          title: "Error",
          description: `Failed to update ${config.label}`,
          variant: "destructive",
        });
      } finally {
        setSaving(null);
      }
    };

    const handleRefresh = async () => {
      console.log(`Refreshing ${config.key}...`);
      await refetch();
    };

    const IconComponent = config.icon;
    const isConfigured = Boolean(localValue && localValue.trim() && !localValue.includes('YOUR_'));

    return (
      <Card className={`bg-white/10 backdrop-blur-md border-white/20 ${config.critical && !isConfigured ? 'border-red-500/50' : ''}`}>
        <CardHeader className="pb-3">
          <CardTitle className="text-white text-sm font-medium flex items-center justify-between">
            <div className="flex items-center gap-2">
              {IconComponent && <IconComponent className="w-4 h-4 text-primary" />}
              <span>{config.label}</span>
              {config.critical && !isConfigured && (
                <Badge className="bg-red-600/20 text-red-400 text-xs">Required</Badge>
              )}
            </div>
            <div className="flex items-center space-x-2">
              {loading ? (
                <div className="w-4 h-4 border border-white/20 border-t-white/60 rounded-full animate-spin" />
              ) : (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleRefresh}
                  className="text-gray-400 hover:text-white p-1"
                >
                  <RefreshCw className="w-3 h-3" />
                </Button>
              )}
            </div>
          </CardTitle>
          {config.description && (
            <p className="text-xs text-gray-400 mt-1">{config.description}</p>
          )}
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label className="text-white text-xs">Value</Label>
              <div className="flex items-center space-x-1">
                {hasChanged && <Badge className="bg-orange-600/20 text-orange-400 text-xs">Modified</Badge>}
                {isConfigured ? (
                  <CheckCircle className="w-3 h-3 text-green-400" />
                ) : (
                  <AlertCircle className="w-3 h-3 text-yellow-400" />
                )}
              </div>
            </div>
            {config.type === 'textarea' ? (
              <Textarea
                value={localValue}
                onChange={(e) => handleInputChange(e.target.value)}
                placeholder={config.placeholder}
                disabled={saving === config.key}
                className="bg-gray-800 border-gray-600 text-white text-sm min-h-[80px]"
                rows={3}
              />
            ) : (
              <Input
                value={localValue}
                onChange={(e) => handleInputChange(e.target.value)}
                placeholder={config.placeholder}
                disabled={saving === config.key}
                className="bg-gray-800 border-gray-600 text-white text-sm"
              />
            )}
          </div>

          <div className="flex items-center justify-between">
            <div className="text-xs text-gray-400">
              {loading ? 'Loading...' : isConfigured ? '✓ Configured' : '⚠ Not configured'}
            </div>
            <Button
              onClick={handleUpdate}
              disabled={!hasChanged || saving === config.key}
              size="sm"
              className="bg-blue-600 hover:bg-blue-700 text-white disabled:opacity-50"
            >
              {saving === config.key ? (
                <div className="w-3 h-3 border border-white/20 border-t-white/60 rounded-full animate-spin mr-1" />
              ) : (
                <Save className="w-3 h-3 mr-1" />
              )}
              Save
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  };

  return (
    <div className="space-y-6">
      {/* Search Engine Verification Section */}
      <Card className="bg-white/10 backdrop-blur-md border-white/20">
        <CardHeader>
          <CardTitle className="text-white flex items-center space-x-2">
            <Shield className="w-5 h-5 text-red-400" />
            <span>Search Engine Verification</span>
            <Badge className="bg-red-600/20 text-red-400 text-xs ml-2">Critical for SEO</Badge>
          </CardTitle>
          <p className="text-sm text-gray-400">
            Add verification codes to prove site ownership to search engines. This enables access to Search Console features.
          </p>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {verificationConfigs.map((config) => (
              <SEOConfigCard key={config.key} config={config} />
            ))}
          </div>
        </CardContent>
      </Card>

      <Separator className="bg-white/10" />

      {/* SEO Meta Settings Section */}
      <Card className="bg-white/10 backdrop-blur-md border-white/20">
        <CardHeader>
          <CardTitle className="text-white flex items-center space-x-2">
            <Search className="w-5 h-5" />
            <span>SEO Meta Settings</span>
          </CardTitle>
          <p className="text-sm text-gray-400">
            Default meta tags used across the site. Individual pages can override these values.
          </p>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {seoConfigs.map((config) => (
              <SEOConfigCard key={config.key} config={config} />
            ))}
          </div>
        </CardContent>
      </Card>

      <Separator className="bg-white/10" />

      {/* Analytics Section */}
      <Card className="bg-white/10 backdrop-blur-md border-white/20">
        <CardHeader>
          <CardTitle className="text-white flex items-center space-x-2">
            <BarChart3 className="w-5 h-5" />
            <span>Analytics & Tracking</span>
          </CardTitle>
          <p className="text-sm text-gray-400">
            Configure analytics and advertising pixels for tracking site performance.
          </p>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {analyticsConfigs.map((config) => (
              <SEOConfigCard key={config.key} config={config} />
            ))}
          </div>
        </CardContent>
      </Card>

      {/* SEO Status Summary */}
      <Card className="bg-gradient-to-r from-primary/20 to-purple-600/20 border-primary/30">
        <CardHeader>
          <CardTitle className="text-white text-sm">SEO Configuration Status</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
            <div className="p-3 bg-white/5 rounded-lg">
              <div className="text-2xl font-bold text-white">8</div>
              <div className="text-xs text-gray-400">Landing Pages</div>
            </div>
            <div className="p-3 bg-white/5 rounded-lg">
              <div className="text-2xl font-bold text-white">40+</div>
              <div className="text-xs text-gray-400">FAQ Items</div>
            </div>
            <div className="p-3 bg-white/5 rounded-lg">
              <div className="text-2xl font-bold text-white">10+</div>
              <div className="text-xs text-gray-400">Blog Posts</div>
            </div>
            <div className="p-3 bg-white/5 rounded-lg">
              <div className="text-2xl font-bold text-green-400">Active</div>
              <div className="text-xs text-gray-400">Sitemap</div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminSiteConfigSEOSettings;